import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Military-Grade Encryption',
      description: 'Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide.'
    },
    {
      icon: 'ri-global-line',
      title: 'Global Server Network',
      description: 'Access our extensive network of high-speed servers strategically located across the globe for optimal performance.'
    },
    {
      icon: 'ri-file-shield-line',
      title: 'Zero-Log Policy',
      description: 'We never track, collect, or share your private data. Your online activity remains completely private and anonymous.'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Lightning Fast Speed',
      description: 'Experience blazing-fast connection speeds without compromising security. Stream, browse, and download seamlessly.'
    },
    {
      icon: 'ri-devices-line',
      title: 'Multi-Device Support',
      description: 'Protect all your devices with a single account. Compatible with Windows, Mac, iOS, Android, and more.'
    },
    {
      icon: 'ri-time-line',
      title: '24/7 Protection',
      description: 'Round-the-clock security monitoring and automatic threat detection keep you safe at all times.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`text-2xl font-bold tracking-tight ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                ITALUNION
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Home</a>
              <a href="#features" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Features</a>
              <a href="#about" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>About</a>
              <a href="#contact" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Contact</a>
            </div>
            <button className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/639169a1e0e508a8028ddcfcecab389e.jpg" 
            alt="Digital Security Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="space-y-6">
            <h1 className="text-6xl md:text-7xl font-bold text-white leading-tight tracking-tight">
              Secure Your<br />
              <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Digital Freedom</span><br />
              With ITALUNION
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto font-light">
              Enterprise-grade VPN protection for the modern internet
            </p>
            <div className="pt-8">
              <button className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white px-10 py-4 rounded-lg text-lg font-medium transition-all shadow-lg hover:shadow-xl whitespace-nowrap inline-flex items-center space-x-2">
                <span>Download Now</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Floating geometric shapes */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-teal-400/10 rounded-full blur-2xl"></div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-24 bg-gradient-to-br from-gray-50 to-teal-50/30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-100/30 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2">
              <div className="space-y-6">
                <div className="text-xs font-bold text-teal-600 tracking-widest uppercase">Who We Are</div>
                <h2 className="text-5xl font-bold text-gray-900 leading-tight">
                  ITALUNION d.o.o.<br />
                  <span className="text-teal-600">Security Innovation</span>
                </h2>
              </div>
            </div>
            
            <div className="md:col-span-3 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                ITALUNION d.o.o. is a leading software development company specializing in cutting-edge cybersecurity solutions. Based in Pula, Croatia, we are dedicated to protecting digital privacy and freedom for users worldwide.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our flagship VPN application combines advanced encryption technology with user-friendly design, ensuring that everyone can access secure, private internet browsing without compromise. We believe that online privacy is a fundamental right, not a luxury.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With a commitment to transparency, innovation, and security excellence, ITALUNION continues to push the boundaries of what's possible in digital protection. Our zero-log policy and military-grade encryption ensure your data remains yours alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-xs font-bold text-teal-600 tracking-widest uppercase mb-4">Features</div>
            <h2 className="text-5xl font-bold text-gray-900 leading-tight">
              Why Choose<br />ITALUNION VPN
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border border-gray-100"
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-gradient-to-br from-gray-900 via-teal-900 to-gray-900 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Secure<br />
            <span className="relative">
              Your Connection?
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-emerald-400"></div>
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join thousands protecting their digital privacy
          </p>
          <button className="bg-white text-gray-900 px-12 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap inline-flex items-center space-x-2 mb-8">
            <span>Get Started Free</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
          
          <div className="flex items-center justify-center space-x-8 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <i className="ri-close-circle-line text-lg"></i>
              <span>No Credit Card</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-flashlight-line text-lg"></i>
              <span>Instant Setup</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-calendar-line text-lg"></i>
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-xs font-bold text-teal-600 tracking-widest uppercase mb-4">Get In Touch</div>
            <h2 className="text-5xl font-bold text-gray-900 leading-tight">Contact Us</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-md text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
              <a href="mailto:contact@italuniondoo.com" className="text-gray-600 hover:text-teal-600 transition-colors">
                contact@italuniondoo.com
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Phone</h3>
              <a href="tel:+12763881395" className="text-gray-600 hover:text-teal-600 transition-colors">
                +1 276 388 1395
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Location</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Trg I. istarske brigade<br />
                Piazza della I brigata istriana 10<br />
                52100 Pula, Croatia
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="text-2xl font-bold text-white mb-4">ITALUNION</div>
              <p className="text-sm leading-relaxed mb-6">
                Protecting digital freedom worldwide
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider mb-4">Company</h4>
              <div className="space-y-2 text-sm">
                <p>Trg I. istarske brigade</p>
                <p>Piazza della I brigata istriana 10</p>
                <p>52100 Pula</p>
                <p>Croatia</p>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider mb-4">Contact</h4>
              <div className="space-y-3 text-sm">
                <a href="mailto:contact@italuniondoo.com" className="flex items-center space-x-2 hover:text-teal-500 transition-colors">
                  <i className="ri-mail-line text-teal-500"></i>
                  <span>contact@italuniondoo.com</span>
                </a>
                <a href="tel:+12763881395" className="flex items-center space-x-2 hover:text-teal-500 transition-colors">
                  <i className="ri-phone-line text-teal-500"></i>
                  <span>+1 276 388 1395</span>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider mb-4">Stay Updated</h4>
              <div className="space-y-3">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:from-teal-600 hover:to-emerald-600 transition-all whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 text-sm">
            <p>&copy; 2025 ITALUNION d.o.o. All rights reserved</p>
            <div className="flex space-x-6">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-teal-500 transition-colors">Powered by Readdy</a>
              <a href="#" className="hover:text-teal-500 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-teal-500 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
